import { Box, Center, HStack, Tooltip, VStack } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import React from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Icon from 'components/Icon';
import Text from 'components/Text';
import UserAvatar from 'components/UserAvatar';
import { useMediaQueries } from 'layout/MediaQueriesProvider';

import NodeTypeIcon from './NodeTypeIcon';
import {
  getDifficultyConfig,
  getNodeTypeRawColor,
  getNodeXpReward,
  NodeState,
} from './dashboardUtils';
import {
  getJourneyPath,
  JourneyNode,
} from './journeyPathConfig';

// ─── Animations ──────────────────────────────────────────────────────────────

const currentPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(183, 148, 244, 0.5); }
  50% { box-shadow: 0 0 0 10px rgba(183, 148, 244, 0); }
`;

const currentBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const floatIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '183,148,244';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
};


// ─── Types ───────────────────────────────────────────────────────────────────

interface JourneyPathProps {
  goal?: string | null;
  experienceLevel?: string | null;
  focusArea?: string | null;
  nodeStatesMap: Map<string, NodeState>;
  totalNodes: number;
  onCustomize?: () => void;
  selectedNodeId?: string | null;
  onNodeSelect?: (node: JourneyNode, globalIndex: number) => void;
  onStartClick?: () => void;
  bottomRef?: React.RefObject<HTMLDivElement>;
}

// ─── Coin Node ───────────────────────────────────────────────────────────────

interface CoinNodeProps {
  node: JourneyNode;
  state: NodeState;
  isSmall: boolean;
  isSelected?: boolean;
  isPlayerHere?: boolean;
  onClick?: () => void;
}

const COIN_SIZE = 52;
const COIN_SIZE_SM = 36;

// Player indicator always uses brand purple
const PLAYER_COLOR = '#B794F4';
const PLAYER_RGB = '183,148,244';
// Completed nodes always use green
const COMPLETED_COLOR = '#68D391';
const COMPLETED_RGB = '104,211,145';

const CoinNode = ({ node, state, isSmall, isSelected, isPlayerHere, onClick }: CoinNodeProps) => {
  const rawColor = getNodeTypeRawColor(node.type);
  const rgb = hexToRgb(rawColor);
  const xp = getNodeXpReward(node.type, node.difficulty);
  const diffConfig = getDifficultyConfig(node.difficulty);
  const isCompleted = state === 'completed';
  const isCurrent = state === 'current';
  const size = isSmall ? COIN_SIZE_SM : COIN_SIZE;
  const showPlayer = isPlayerHere ?? isCurrent;

  const tooltipContent = (
    <VStack spacing={0.5} align="flex-start" py={0.5}>
      <Box as="span" style={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>
        {node.label}
      </Box>
      {(diffConfig || node.subtitle) && (
        <Box as="span" style={{ fontSize: '10px', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
          {diffConfig ? `${diffConfig.label} ${node.type === 'project' ? 'Project' : 'Problem'}` : node.subtitle}
        </Box>
      )}
      {xp > 0 && (
        <Box as="span" style={{ fontSize: '10px', fontWeight: 700, color: rawColor }}>
          +{xp} XP
        </Box>
      )}
    </VStack>
  );

  return (
    <Tooltip
      label={tooltipContent}
      placement="top"
      hasArrow
      bg="#1A202C"
      borderRadius="8px"
      px={3}
      py={2}
      border="1px solid"
      borderColor="whiteAlpha.200"
      boxShadow="0 8px 24px rgba(0,0,0,0.4)"
    >
      <VStack

        spacing={1}
        cursor="pointer"
        onClick={onClick}
        role="group"
        flex={1}
        align="center"
        overflow="visible"
      >
        {/* The coin itself */}
        <Box position="relative">
          {/* Outer ring for player position — always purple */}
          {showPlayer && (
            <Box
              position="absolute"
              top="-4px"
              left="-4px"
              right="-4px"
              bottom="-4px"
              borderRadius="full"
              animation={`${currentPulse} 2s ease-in-out infinite`}
              style={{
                border: `2px solid rgba(${PLAYER_RGB}, 0.6)`,
              }}
            />
          )}

          <Center
            w={`${size}px`}
            h={`${size}px`}
            borderRadius="full"
            position="relative"
            transition="all 0.2s ease"
            animation={showPlayer ? `${currentBounce} 2s ease-in-out infinite` : undefined}
            _groupHover={{
              transform: showPlayer ? undefined : 'scale(1.08)',
            }}
            style={{
              background: isCompleted
                ? `linear-gradient(145deg, rgba(${COMPLETED_RGB}, 0.3) 0%, rgba(${COMPLETED_RGB}, 0.12) 100%)`
                : showPlayer
                  ? `linear-gradient(145deg, rgba(${PLAYER_RGB}, 0.25) 0%, rgba(${PLAYER_RGB}, 0.08) 100%)`
                  : isSelected
                    ? `linear-gradient(145deg, rgba(${rgb}, 0.15) 0%, rgba(${rgb}, 0.05) 100%)`
                    : `linear-gradient(145deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)`,
              border: isCompleted
                ? `2px solid rgba(${COMPLETED_RGB}, 0.5)`
                : showPlayer
                  ? `2px solid rgba(${PLAYER_RGB}, 0.7)`
                  : isSelected
                    ? `2px solid rgba(${rgb}, 0.8)`
                    : '2px solid rgba(255,255,255,0.08)',
              boxShadow: isCompleted
                ? `0 0 16px rgba(${COMPLETED_RGB}, 0.2), inset 0 0 12px rgba(${COMPLETED_RGB}, 0.08)`
                : showPlayer
                  ? `0 0 20px rgba(${PLAYER_RGB}, 0.3), inset 0 0 16px rgba(${PLAYER_RGB}, 0.1)`
                  : isSelected
                    ? `0 0 12px rgba(${rgb}, 0.3)`
                    : 'inset 0 1px 0 rgba(255,255,255,0.06)',
              transform: isSelected && !showPlayer ? 'scale(1.05)' : undefined,
            }}
          >
            {isCompleted ? (
              <Center
                w="24px"
                h="24px"
                borderRadius="full"
                style={{
                  background: `linear-gradient(135deg, ${COMPLETED_COLOR}, rgba(${COMPLETED_RGB}, 0.7))`,
                  boxShadow: `0 0 8px rgba(${COMPLETED_RGB}, 0.5)`,
                }}
              >
                <Box as="span" style={{ fontSize: '13px', fontWeight: 700, color: '#1A202C' }}>
                  ✓
                </Box>
              </Center>
            ) : (
              <Box
                style={{
                  filter: showPlayer
                    ? `drop-shadow(0 0 6px rgba(${PLAYER_RGB}, 0.5))`
                    : isSelected
                      ? `drop-shadow(0 0 6px rgba(${rgb}, 0.5))`
                      : undefined,
                  color: showPlayer ? PLAYER_COLOR : isSelected ? rawColor : 'rgba(255,255,255,0.6)',
                }}
              >
                <NodeTypeIcon
                  type={node.type}
                  size={isSmall ? 16 : 18}
                  color={showPlayer ? PLAYER_COLOR : isSelected ? rawColor : undefined}
                />
              </Box>
            )}
          </Center>

          {/* YOU badge — always purple */}
          {showPlayer && (
            <Center
              position="absolute"
              bottom="-6px"
              left="50%"
              transform="translateX(-50%)"
              px={1.5}
              borderRadius="full"
              style={{
                background: `linear-gradient(135deg, ${PLAYER_COLOR}, rgba(${PLAYER_RGB}, 0.8))`,
                boxShadow: `0 2px 8px rgba(${PLAYER_RGB}, 0.5)`,
                height: '14px',
              }}
            >
              <Box
                as="span"
                style={{
                  fontSize: '7px',
                  fontWeight: 800,
                  color: '#1A202C',
                  letterSpacing: '0.06em',
                }}
              >
                YOU
              </Box>
            </Center>
          )}

        </Box>

        {/* Label below coin */}
        <Box
          textAlign="center"
          mt={showPlayer || isCompleted ? '2px' : 0}
          maxW={`${size + (isSmall ? 16 : 40)}px`}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Box
            as="span"
            style={{
              fontSize: isSmall ? '9px' : '10px',
              fontWeight: 600,
              lineHeight: '1.2',
              color: showPlayer
                ? PLAYER_COLOR
                : isSelected
                  ? rawColor
                  : isCompleted
                    ? `rgba(${COMPLETED_RGB}, 0.8)`
                    : 'rgba(255,255,255,0.4)',
              textShadow: showPlayer
                ? `0 0 8px rgba(${PLAYER_RGB}, 0.3)`
                : isSelected
                  ? `0 0 8px rgba(${rgb}, 0.3)`
                  : undefined,
            }}
          >
            {node.label}
          </Box>
        </Box>
      </VStack>
    </Tooltip>
  );
};

// ─── Connectors ─────────────────────────────────────────────────────────────

const CONNECTOR_SIZE = 24;

const connectorStyle = (isCompleted: boolean, direction: 'horizontal' | 'vertical') => {
  const deg = direction === 'horizontal' ? '90deg' : '180deg';
  return {
    background: isCompleted
      ? `linear-gradient(${deg}, #B794F4, #9F7AEA)`
      : undefined,
    backgroundImage: isCompleted
      ? undefined
      : `repeating-linear-gradient(${deg}, rgba(255,255,255,0.12) 0, rgba(255,255,255,0.12) 3px, transparent 3px, transparent 7px)`,
  };
};

const HorizontalConnector = ({ isCompleted }: { isCompleted: boolean }) => (
  <Box h="2px" w="100%" borderRadius="full" style={connectorStyle(isCompleted, 'horizontal')} />
);

const VerticalConnector = ({ isCompleted }: { isCompleted: boolean }) => (
  <Center w="100%" h={`${CONNECTOR_SIZE}px`}>
    <Box w="2px" h="100%" borderRadius="full" style={connectorStyle(isCompleted, 'vertical')} />
  </Center>
);

// ─── Section Checkpoint Bar ──────────────────────────────────────────────────

interface CheckpointProps {
  label: string;
  isCompleted: boolean;
  totalXp: number;
  earnedXp: number;
  sectionIndex: number;
}

const SectionCheckpoint = ({ label, isCompleted, totalXp, earnedXp, sectionIndex }: CheckpointProps) => {
  const checkpointIcons = ['🎯', '⚡', '🚀', '🏆'];
  const icon = isCompleted ? '🏆' : checkpointIcons[sectionIndex % checkpointIcons.length];

  return (
    <HStack
      w="100%"
      py={2}
      px={4}
      borderRadius="lg"
      justify="center"
      spacing={2.5}
      border="1px solid"
      borderColor={isCompleted ? 'rgba(159, 122, 234, 0.3)' : 'whiteAlpha.200'}
      style={{
        background: isCompleted
          ? 'linear-gradient(90deg, rgba(159, 122, 234, 0.12) 0%, rgba(237, 137, 54, 0.06) 100%)'
          : 'rgba(255,255,255,0.05)',
      }}
    >
      <Box
        style={{
          fontSize: isCompleted ? '14px' : '12px',
          opacity: isCompleted ? 1 : 0.65,
          filter: isCompleted ? 'drop-shadow(0 0 4px rgba(159, 122, 234, 0.4))' : undefined,
        }}
      >
        {icon}
      </Box>
      <Text
        fontSize="micro"
        fontWeight="semibold"
        opacity={isCompleted ? 'primary' : 'secondary'}
      >
        {label}
      </Text>
      <Center
        px={2}
        py={0.5}
        borderRadius="full"
        style={{
          backgroundColor: isCompleted ? 'rgba(183, 148, 244, 0.15)' : 'rgba(255,255,255,0.06)',
          border: isCompleted ? '1px solid rgba(183, 148, 244, 0.25)' : '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box
          as="span"
          style={{
            fontSize: '9px',
            fontWeight: 700,
            lineHeight: 1,
            color: isCompleted ? '#B794F4' : 'rgba(255,255,255,0.45)',
          }}
        >
          {earnedXp}/{totalXp} XP
        </Box>
      </Center>
    </HStack>
  );
};

// ─── Section Component ───────────────────────────────────────────────────────

interface SectionProps {
  section: { title: string; checkpointLabel: string; nodes: JourneyNode[] };
  sectionIndex: number;
  sectionStartIndex: number;
  nodeStatesMap: Map<string, NodeState>;
  isSmall: boolean;
  selectedNodeId?: string | null;
  onNodeClick: (node: JourneyNode, globalIndex: number) => void;
  animDelay: number;
  allCompleted: boolean;
  totalNodes: number;
}

const JourneySection = ({
  section,
  sectionIndex,
  sectionStartIndex,
  nodeStatesMap,
  isSmall,
  selectedNodeId,
  onNodeClick,
  animDelay,
  allCompleted,
  totalNodes,
}: SectionProps) => {
  const isSectionCompleted = section.nodes.every(
    (n) => nodeStatesMap.get(n.id) === 'completed'
  );

  let totalXp = 0;
  let earnedXp = 0;
  for (const node of section.nodes) {
    const nodeXp = getNodeXpReward(node.type, node.difficulty);
    totalXp += nodeXp;
    if (nodeStatesMap.get(node.id) === 'completed') {
      earnedXp += nodeXp;
    }
  }

  return (
    <VStack
      spacing={2}
      align="stretch"
      animation={`${floatIn} 0.4s ease forwards`}
      style={{ animationDelay: `${animDelay}ms`, opacity: 0 }}
    >
      <HStack
        spacing={0}
        align="flex-start"
        w="100%"
        py={2}
      >
        {section.nodes.map((node, nodeIdx) => {
          const globalIdx = sectionStartIndex + nodeIdx;
          const state = nodeStatesMap.get(node.id) ?? 'available';
          const coinSize = isSmall ? COIN_SIZE_SM : COIN_SIZE;
          const prevNode = nodeIdx > 0 ? section.nodes[nodeIdx - 1] : null;
          const prevCompleted = prevNode ? nodeStatesMap.get(prevNode.id) === 'completed' : false;

          return (
            <Box key={node.id} display="contents">
              {nodeIdx > 0 && (
                <Center flex={1} pt={`${coinSize / 2 - 1}px`}>
                  <HorizontalConnector
                    isCompleted={prevCompleted && state === 'completed'}
                  />
                </Center>
              )}
              <CoinNode
                node={node}
                state={state}
                isSmall={isSmall}
                isSelected={selectedNodeId === node.id}
                isPlayerHere={
                  selectedNodeId
                    ? selectedNodeId === node.id
                    : allCompleted
                      ? globalIdx === Math.floor(totalNodes / 2)
                      : false
                }
                onClick={() => onNodeClick(node, globalIdx)}
              />
            </Box>
          );
        })}
      </HStack>

      <SectionCheckpoint
        label={section.checkpointLabel}
        isCompleted={isSectionCompleted}
        totalXp={totalXp}
        earnedXp={earnedXp}
        sectionIndex={sectionIndex}
      />
    </VStack>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const JourneyPath = ({ goal, experienceLevel, focusArea, nodeStatesMap, totalNodes, onCustomize, selectedNodeId, onNodeSelect, onStartClick, bottomRef }: JourneyPathProps) => {
  const { isSmallScreen } = useMediaQueries();
  const { avatarUrl, experience, name } = useCurrentUser();
  const sections = getJourneyPath(goal, experienceLevel, focusArea);
  const completedNodeCount = Array.from(nodeStatesMap.values()).filter((s) => s === 'completed').length;
  const allCompleted = completedNodeCount >= totalNodes && totalNodes > 0;

  const handleNodeClick = (node: JourneyNode, globalIndex: number) => {
    if (onNodeSelect) {
      onNodeSelect(node, globalIndex);
    }
  };

  // Render bottom-to-top: reverse so section 0 (first steps) is at the bottom
  const reversedSections = [...sections]
    .map((section, originalIndex) => ({ section, originalIndex }))
    .reverse();

  // Pre-calculate section start indices
  const sectionStartIndices: number[] = [];
  let runningIndex = 0;
  for (const section of sections) {
    sectionStartIndices.push(runningIndex);
    runningIndex += section.nodes.length;
  }

  return (
    <Box
      w="100%"
      p={isSmallScreen ? 3 : 5}
      borderRadius="xl"
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
    >
      <HStack mb={4} spacing={2} justify="space-between">
        <HStack spacing={2}>
          <Icon type="SidebarDashboard" size={20} />
          <Text fontSize="large" fontWeight="semibold">
            Your Journey
          </Text>
        </HStack>
        {onCustomize && (
          <HStack
            as="button"
            spacing={1}
            onClick={onCustomize}
            opacity={0.35}
            _hover={{ opacity: 0.8 }}
            transition="opacity 0.2s"
            cursor="pointer"
          >
            <Icon type="Settings" size={14} />
            <Text fontSize="xMicro" opacity="secondary">
              Customize
            </Text>
          </HStack>
        )}
      </HStack>

      <VStack spacing={0} align="stretch">
        {reversedSections.map(({ section, originalIndex }, displayIndex) => {
          const sectionStart = sectionStartIndices[originalIndex];
          const isLast = displayIndex === reversedSections.length - 1;

          return (
            <Box key={section.title}>
              <JourneySection
                section={section}
                sectionIndex={originalIndex}
                sectionStartIndex={sectionStart}
                nodeStatesMap={nodeStatesMap}
                isSmall={isSmallScreen}
                selectedNodeId={selectedNodeId}
                onNodeClick={handleNodeClick}
                animDelay={displayIndex * 100}
                allCompleted={allCompleted}
                totalNodes={totalNodes}
              />
              {!isLast && (
                <VerticalConnector
                  isCompleted={section.nodes.every(
                    (n) => nodeStatesMap.get(n.id) === 'completed'
                  )}
                />
              )}
            </Box>
          );
        })}

        {/* START position — always shown at the bottom of the journey.
            No completions + no selection = player is here (YOU badge + pulse).
            No completions + node selected = completed checkpoint (green checkmark).
            Has completions = always completed checkpoint (green checkmark). */}
        {(() => {
          const isPlayerOnStart = completedNodeCount === 0 && !selectedNodeId;
          const size = isSmallScreen ? COIN_SIZE_SM : COIN_SIZE;
          return (
            <>
              <VerticalConnector isCompleted={!isPlayerOnStart} />
              <Center w="100%">
                <VStack
                  spacing={1}
                  cursor={completedNodeCount === 0 ? 'pointer' : 'default'}
                  onClick={completedNodeCount === 0 ? onStartClick : undefined}
                  role="group"
                >
                  <Box position="relative">
                    {isPlayerOnStart && (
                      <Box
                        position="absolute"
                        top="-4px"
                        left="-4px"
                        right="-4px"
                        bottom="-4px"
                        borderRadius="full"
                        animation={`${currentPulse} 2s ease-in-out infinite`}
                        style={{ border: '2px solid rgba(183, 148, 244, 0.6)' }}
                      />
                    )}
                    <Center
                      w={`${size}px`}
                      h={`${size}px`}
                      borderRadius="full"
                      border="2px solid"
                      borderColor={isPlayerOnStart ? 'purple.400' : 'green.400'}
                      animation={isPlayerOnStart ? `${currentBounce} 2s ease-in-out infinite` : undefined}
                      overflow="hidden"
                      transition="all 0.2s ease"
                      _groupHover={completedNodeCount === 0 ? { transform: 'scale(1.05)' } : undefined}
                      style={{
                        background: isPlayerOnStart
                          ? undefined
                          : 'linear-gradient(145deg, rgba(104, 211, 145, 0.2) 0%, rgba(104, 211, 145, 0.08) 100%)',
                      }}
                    >
                      {isPlayerOnStart ? (
                        <UserAvatar
                          name={name}
                          src={avatarUrl}
                          experience={experience}
                          size={isSmallScreen ? 'sm' : 'md'}
                          hideCircularProgress
                        />
                      ) : (
                        <Center
                          w="24px"
                          h="24px"
                          borderRadius="full"
                          style={{
                            background: 'linear-gradient(135deg, #68D391, rgba(104, 211, 145, 0.7))',
                            boxShadow: '0 0 8px rgba(104, 211, 145, 0.5)',
                          }}
                        >
                          <Box as="span" style={{ fontSize: '13px', fontWeight: 700, color: '#1A202C' }}>
                            ✓
                          </Box>
                        </Center>
                      )}
                    </Center>
                    {isPlayerOnStart && (
                      <Center
                        position="absolute"
                        bottom="-6px"
                        left="50%"
                        transform="translateX(-50%)"
                        px={1.5}
                        borderRadius="full"
                        style={{
                          background: 'linear-gradient(135deg, #B794F4, rgba(183, 148, 244, 0.8))',
                          boxShadow: '0 2px 8px rgba(183, 148, 244, 0.5)',
                          height: '14px',
                        }}
                      >
                        <Box
                          as="span"
                          style={{ fontSize: '7px', fontWeight: 800, color: '#1A202C', letterSpacing: '0.06em' }}
                        >
                          YOU
                        </Box>
                      </Center>
                    )}
                  </Box>
                  <Box
                    as="span"
                    mt={isPlayerOnStart ? '2px' : 0}
                    style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: isPlayerOnStart ? '#B794F4' : '#68D391',
                      letterSpacing: '0.08em',
                    }}
                  >
                    START
                  </Box>
                </VStack>
              </Center>
            </>
          );
        })()}
      </VStack>

      {/* Scroll anchor at the bottom of the journey */}
      <div ref={bottomRef} />
    </Box>
  );
};

export default JourneyPath;
